import { Stack, Typography } from '@mui/material';
import type { KeyboardEvent } from 'react';

import { type EventListItemProps, useEventListItem } from './useEventListItem';

type Props = EventListItemProps;

export function EventListItem(props: Props) {
  const hook = useEventListItem(props);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (props.onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      props.onClick();
    }
  }

  const secondaryLabel = props.subtitle
    ? `${props.subtitle} · ${hook.formattedDate} (${hook.timeText})`
    : hook.providerName
      ? `${hook.providerName} · ${hook.formattedDate} (${hook.timeText})`
      : `${hook.formattedDate} · ${hook.timeText}`;

  return (
    <Stack
      direction="row"
      spacing={1.5}
      data-testid={props['data-testid']}
      onClick={props.onClick}
      onKeyDown={props.onClick ? handleKeyDown : undefined}
      tabIndex={props.onClick ? 0 : undefined}
      role={props.onClick ? 'button' : undefined}
      sx={(theme) => ({
        alignItems: 'center',
        p: 1,
        cursor: props.onClick ? 'pointer' : 'default',
        ...(props.onClick && {
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }),
      })}
    >
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          height: 46,
          flexShrink: 0,
          borderRadius: 1,
          bgcolor: 'background.elevation1',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="body1Emphasis"
        >
          {hook.dayNumber}
        </Typography>

        <Typography
          variant="body2Emphasis"
          sx={{
            color: hook.accentColor,
            textTransform: 'uppercase',
          }}
        >
          {hook.monthAbbr}
        </Typography>
      </Stack>

      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
        <Typography variant="body1Emphasis" noWrap>
          {props.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {secondaryLabel}
        </Typography>
      </Stack>

      {props.action ? (
        <Stack sx={{ flexShrink: 0 }}>
          {props.action}
        </Stack>
      ) : null}
    </Stack>
  );
}

export type { EventListItemProps };
